import React, { createContext, useContext, useState, useEffect } from 'react';
import { heroSlideAPI } from '../services/api';
import logger from '../utils/logger';

const HeroContext = createContext();

export const useHero = () => {
  const context = useContext(HeroContext);
  if (!context) {
    throw new Error('useHero must be used within a HeroProvider');
  }
  return context;
};

export const HeroProvider = ({ children }) => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch slides from API with retry logic
  const fetchSlides = async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);
      const response = await heroSlideAPI.getSlides(false);
      // API interceptor returns response.data directly, so check both structures
      const slidesData = response.data || (Array.isArray(response) ? response : []);
      // Map database _id to id for compatibility
      const mappedSlides = slidesData.map(slide => ({
        id: slide._id || slide.id,
        _id: slide._id,
        title: slide.title,
        subtitle: slide.subtitle,
        image: slide.image,
        isActive: slide.isActive,
        order: slide.order || 0,
        duration: slide.duration || 5000, // Default 5 seconds
        createdAt: slide.createdAt,
        updatedAt: slide.updatedAt
      }));
      setSlides(mappedSlides);
    } catch (err) {
      logger.error('Error fetching hero slides:', err);
      
      // Check for ServiceWorker errors
      if (err.message?.includes('ServiceWorker') || err.message?.includes('service-worker')) {
        logger.warn('ServiceWorker may be interfering with requests. Try clearing browser cache or disabling ServiceWorker.');
      }
      
      // Handle timeout errors with retry
      if ((err.code === 'ECONNABORTED' || err.message?.includes('timeout')) && retryCount < 2) {
        logger.debug(`Retrying hero slides fetch (attempt ${retryCount + 1}/2)...`);
        // Wait 2 seconds before retry
        await new Promise(resolve => setTimeout(resolve, 2000));
        return fetchSlides(retryCount + 1);
      }
      
      // Only set error if we've exhausted retries or it's not a timeout
      if (retryCount >= 2 || (err.code !== 'ECONNABORTED' && !err.message?.includes('timeout'))) {
        const errorMessage = err.message || 'Failed to fetch hero slides';
        // Don't show ServiceWorker errors to users, just log them
        if (!errorMessage.includes('ServiceWorker')) {
          setError(errorMessage);
        }
      }
      
      // Keep existing slides if available, don't clear them on timeout
      setSlides(prevSlides => {
        if (prevSlides.length === 0) {
          return [];
        }
        return prevSlides; // Keep existing slides
      });
    } finally {
      setLoading(false);
    }
  };

  // Initialize slides from API
  useEffect(() => {
    fetchSlides();
  }, []);

  // Refresh slides periodically (every 60 seconds) to sync with other devices
  // Only refresh if not currently loading and no recent error
  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading && !error) {
        fetchSlides();
      }
    }, 60000); // 60 seconds - reduced frequency to avoid timeout issues

    return () => clearInterval(interval);
  }, [loading, error]);

  const addSlide = async (newSlideData) => {
    try {
      const response = await heroSlideAPI.createSlide({
        title: newSlideData.title,
        subtitle: newSlideData.subtitle,
        image: newSlideData.image,
        isActive: newSlideData.isActive !== undefined ? newSlideData.isActive : true,
        duration: newSlideData.duration || 5000
      });
      
      const newSlide = {
        id: response.data.id || response.data._id,
        _id: response.data.id || response.data._id,
        title: response.data.title,
        subtitle: response.data.subtitle,
        image: response.data.image,
        isActive: response.data.isActive,
        order: response.data.order || 0,
        duration: response.data.duration || 5000
      };
      
      setSlides(prevSlides => [...prevSlides, newSlide]);
      return newSlide;
    } catch (err) {
      logger.error('Error creating hero slide:', err);
      throw err;
    }
  };

  const updateSlide = async (slideId, updatedData) => {
    try {
      // Find the slide to get the _id if slideId is the old id format
      const slide = slides.find(s => s.id === slideId || s._id === slideId);
      const dbId = slide?._id || slideId;
      
      const response = await heroSlideAPI.updateSlide(dbId, updatedData);
      
      const updatedSlide = {
        id: response.data._id || response.data.id,
        _id: response.data._id || response.data.id,
        title: response.data.title,
        subtitle: response.data.subtitle,
        image: response.data.image,
        isActive: response.data.isActive,
        order: response.data.order || 0,
        duration: response.data.duration || 5000
      };
      
      setSlides(prevSlides => 
        prevSlides.map(slide => 
          (slide.id === slideId || slide._id === slideId) ? updatedSlide : slide
        )
      );
      
      return updatedSlide;
    } catch (err) {
      logger.error('Error updating hero slide:', err);
      throw err;
    }
  };

  const deleteSlide = async (slideId) => {
    try {
      // Find the slide to get the _id if slideId is the old id format
      const slide = slides.find(s => s.id === slideId || s._id === slideId);
      const dbId = slide?._id || slideId;
      
      await heroSlideAPI.deleteSlide(dbId);
      
      setSlides(prevSlides => prevSlides.filter(slide => 
        slide.id !== slideId && slide._id !== slideId
      ));
    } catch (err) {
      logger.error('Error deleting hero slide:', err);
      throw err;
    }
  };

  const toggleSlideActive = async (slideId) => {
    try {
      const slide = slides.find(s => s.id === slideId || s._id === slideId);
      if (slide) {
        await updateSlide(slideId, { isActive: !slide.isActive });
      }
    } catch (err) {
      logger.error('Error toggling slide active status:', err);
      throw err;
    }
  };

  const reorderSlides = async (fromIndex, toIndex) => {
    try {
      const updatedSlides = [...slides];
      const [removed] = updatedSlides.splice(fromIndex, 1);
      updatedSlides.splice(toIndex, 0, removed);
      
      // Update order values
      const slideOrders = updatedSlides.map((slide, index) => ({
        id: slide._id || slide.id,
        order: index
      }));
      
      await heroSlideAPI.reorderSlides(slideOrders);
      
      // Refresh slides from API to get updated order
      await fetchSlides();
    } catch (err) {
      logger.error('Error reordering slides:', err);
      throw err;
    }
  };

  // Get only active slides for display on homepage
  const getActiveSlides = () => {
    return slides.filter(slide => slide.isActive).sort((a, b) => 
      (a.order || 0) - (b.order || 0)
    );
  };

  const value = {
    slides,
    loading,
    error,
    addSlide,
    updateSlide,
    deleteSlide,
    toggleSlideActive,
    reorderSlides,
    getActiveSlides,
    refreshSlides: fetchSlides
  };

  return (
    <HeroContext.Provider value={value}>
      {children}
    </HeroContext.Provider>
  );
}; 